import { useEffect, useState } from "react";
import { Bot, PhoneCall, Voicemail, Clock, Hand, Utensils } from "lucide-react";
import { api } from "@/lib/api";

const Tile = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-xl border border-border bg-background/60 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground"><Icon className="w-4 h-4" /><span className="label-overline">{label}</span></div>
    <div className="font-display text-3xl font-black tracking-tight mt-2">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

// What the AI assistant + phone receptionist handled, and the advisor time that saved.
export const AiActivityCard = ({ days }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/workshop/ai-activity?days=${days}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [days]);
  if (!data) return null;
  const t = data.totals;
  const max = Math.max(1, ...data.by_day.map((d) => d.replies + d.calls + d.voicemails));
  const idle = !data.ai_advisor_enabled && !data.phone_receptionist_enabled && t.replies + t.calls === 0;

  return (
    <section className="card-default p-6" data-testid="ai-activity-card">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-overline">AI assistant activity · last {days} days</div>
          <h2 className="font-display text-xl font-bold mt-1">
            {t.hours_saved > 0 ? <>About <span className="text-primary">{t.hours_saved} hours</span> of desk time handled by the assistant.</> : "The assistant hasn't handled anything in this period."}
          </h2>
        </div>
        <div className="text-xs text-muted-foreground">
          {data.ai_advisor_enabled ? "Texts: on" : "Texts: off"} · {data.phone_receptionist_enabled ? "Calls: on" : "Calls: off"}
        </div>
      </div>

      {idle ? (
        <p className="text-sm text-muted-foreground mt-4" data-testid="ai-activity-idle">Switch on the AI assistant in Settings, or tap “We're at lunch” on the Advisor Desk, and this report fills in by itself.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
            <Tile icon={Bot} label="Questions answered" value={t.replies} sub={`${t.customers_helped} customer${t.customers_helped === 1 ? "" : "s"} helped`} testid="ai-activity-replies" />
            <Tile icon={Hand} label="Handed to a person" value={t.handovers} sub={data.answered_share != null ? `${data.answered_share}% resolved by AI` : "price, booking, approval"} testid="ai-activity-handovers" />
            <Tile icon={PhoneCall} label="Calls answered" value={t.calls} sub={t.transfers ? `${t.transfers} passed to the desk` : "status read to caller"} testid="ai-activity-calls" />
            <Tile icon={Voicemail} label="Messages taken" value={t.voicemails} sub="transcribed into the job" testid="ai-activity-voicemails" />
            <Tile icon={Clock} label="Hours saved" value={t.hours_saved} sub={`${t.minutes_saved} min · estimate`} testid="ai-activity-hours" />
          </div>

          {data.by_day.length > 0 && (
            <div className="mt-6" data-testid="ai-activity-chart">
              <div className="flex items-end gap-[3px] h-24">
                {data.by_day.map((d) => {
                  const total = d.replies + d.calls + d.voicemails;
                  return (
                    <div key={d.date} className="flex-1 flex flex-col justify-end gap-px" title={`${d.date}: ${d.replies} answered · ${d.calls} calls · ${d.voicemails} messages · ${d.handovers} handed over · ${d.minutes_saved} min saved`}>
                      <div className="bg-primary/80 rounded-t" style={{ height: `${(d.replies / max) * 100}%` }} />
                      <div className="bg-sky-500/70" style={{ height: `${(d.calls / max) * 100}%` }} />
                      <div className="bg-amber-500/70 rounded-b" style={{ height: `${(d.voicemails / max) * 100}%` }} />
                      {total === 0 && <div className="h-px bg-border" />}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[11px] text-muted-foreground mt-1">
                <span>{data.by_day[0].date}</span>
                <span className="flex gap-3"><span><i className="inline-block w-2 h-2 bg-primary/80 rounded-sm mr-1" />texts answered</span><span><i className="inline-block w-2 h-2 bg-sky-500/70 rounded-sm mr-1" />calls</span><span><i className="inline-block w-2 h-2 bg-amber-500/70 rounded-sm mr-1" />messages</span></span>
                <span>{data.by_day[data.by_day.length - 1].date}</span>
              </div>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground mt-4" data-testid="ai-activity-assumptions">
            Time saved is an estimate: {data.assumptions.reply} min per question answered, {data.assumptions.call} min per call, {data.assumptions.voicemail} min per message taken, {data.assumptions.transfer} min per transfer. Handovers count as zero — a person still dealt with them.
            {t.lunch_covers > 0 && ` Lunch cover used ${t.lunch_covers} time${t.lunch_covers === 1 ? "" : "s"}.`}
          </p>
        </>
      )}
    </section>
  );
};
